/**
 * This file is copied from https://code.amazon.com/packages/OppyPageAssets
 * as a a quick implementation to unblock the launch of tactical recommendations
 * card version 2.
 *
 */

import AdvertisingApiClient from 'advertising-api-javascript-client';
import { logger } from 'src/commons/utils/logger';
import {
  AdvertisingApiClientProps,
  AdvertisingApiClientRequest,
} from './advertising-api-client.interface';
import { getAdvertisingApiCsrfToken } from 'src/commons/apis';

export const csrfTokenRetrieveFn = async () => {
  try {
    const csrfToken = await getAdvertisingApiCsrfToken();
    return csrfToken;
  } catch (e: any) {
    logger.error('Failed to fetch CSRF token from navState', e);
    // Callers treat an absent token as "could not authenticate"; the error is
    // swallowed here deliberately so a CSRF fetch failure does not reject.
    return undefined;
  }
};

export class AdvertisingApiClientProxy {
  // Assigned in the constructor via setApiClient(); tsc cannot see through the call.
  private advertisingApiClient!: AdvertisingApiClient;
  private isInitialized = false;

  constructor(advertisingApiClient: AdvertisingApiClient) {
    this.setApiClient(advertisingApiClient);
  }

  getAdvertisingApiClient() {
    return this.advertisingApiClient;
  }

  init({
    marketplaceId,
    entityId,
    csrfToken,
    clientId,
    endpoint,
    isImpersonator,
    autoRefreshCsrfToken,
  }: AdvertisingApiClientProps) {
    this.advertisingApiClient.setMarketplaceId(marketplaceId);
    this.advertisingApiClient.setEntityId(entityId);
    this.advertisingApiClient.setCsrfToken(csrfToken);
    this.advertisingApiClient.setClientId(clientId);
    this.advertisingApiClient.setEndpoint(endpoint);
    this.advertisingApiClient.setAutoRefreshCsrfToken(
      autoRefreshCsrfToken || false,
    );
    this.advertisingApiClient.setCsrfTokenRetrieveFn(csrfTokenRetrieveFn);
    this.setIsImpersonator(isImpersonator);
    this.isInitialized = true;
  }

  // for testing
  setApiClient(advertisingApiClient: AdvertisingApiClient) {
    this.advertisingApiClient = advertisingApiClient;
  }

  setIsImpersonator(isImpersonator: boolean) {
    this.advertisingApiClient.setIsImpersonator(isImpersonator);
  }

  isImpersonator() {
    return this.advertisingApiClient.isImpersonator;
  }

  // see https://tiny.amazon.com/104yw7hb7/codeamazpackAdveblob28aasrc
  makeRequest(request: AdvertisingApiClientRequest) {
    this._checkIfApiClientInitialized();

    const fetchPromise = this.advertisingApiClient.makeRequest(request);
    // todo: add metrics for API requests through Katal

    return fetchPromise;
  }
  _checkIfApiClientInitialized() {
    if (!this.isInitialized) {
      logger.fatal(
        'An attempt was made to call AdvertisingApiClient when it is not correctly initialized. ' +
          'No call will be made',
      );
      throw new Error(
        'Error calling AdvertisingApiClient, it is not initialized',
      );
    }
  }
}

export const advertisingApiClient = new AdvertisingApiClientProxy(
  new AdvertisingApiClient({}),
);
